import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export function useMapleMarketLicenses(skus: string[]) {
  const { user } = useAuth();
  const [ownedSkus, setOwnedSkus] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const skuKey = useMemo(() => Array.from(new Set(skus.filter(Boolean))).join('|'), [skus]);
  const normalizedSkus = useMemo(() => (skuKey ? skuKey.split('|') : []), [skuKey]);

  useEffect(() => {
    const handleRefresh = () => setRefreshKey((current) => current + 1);
    window.addEventListener('maple-market:licenses-refresh', handleRefresh);
    return () => window.removeEventListener('maple-market:licenses-refresh', handleRefresh);
  }, []);

  useEffect(() => {
    if (!user || normalizedSkus.length === 0) {
      setOwnedSkus([]);
      return;
    }

    let cancelled = false;

    const loadLicenses = async () => {
      const { data, error } = await supabase
        .from('mapleos_product_licenses')
        .select('sku')
        .in('sku', normalizedSkus)
        .eq('status', 'active');

      if (cancelled) {
        return;
      }

      if (error) {
        setOwnedSkus([]);
        return;
      }

      setOwnedSkus((data ?? []).map((license) => license.sku));
    };

    void loadLicenses();

    return () => {
      cancelled = true;
    };
  }, [user, skuKey, refreshKey]);

  return useMemo(() => new Set(ownedSkus), [ownedSkus]);
}